import { FileCopy } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  type DialogProps,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useSetAtom } from 'jotai';
import * as React from 'react';
import { endpoint } from '../env';
import { adminDetailsJotaiAtom, popupAlertAtom } from '../jotai/atoms';
import { requestWithAuth } from '../store/requestWithAuth';
import { analytics } from '../utils/analytics';

export interface InviteDialogProps extends DialogProps {
  teamId: string;
}

type InviteAdmin = { teamId: string; email: string; name: string };

const styles = {
  content: {
    minWidth: 400,
    '& > *': {
      marginBottom: 4
    }
  }
} as const;

export function InviteDialog({ teamId, ...props }: InviteDialogProps) {
  // 追加したいユーザーのメールアドレスと表示名
  const [email, setEmail] = React.useState('');
  const invalid = !/.+@.+\..+/.test(email);
  const [name, setName] = React.useState('');

  // 送信
  const [sending, setSending] = React.useState(false);
  const [successed, setSuccessed] = React.useState(false); // 招待に成功した
  const setPopupAlert = useSetAtom(popupAlertAtom);
  const setAdminDetails = useSetAtom(adminDetailsJotaiAtom(teamId));
  const send = React.useCallback(
    async (params: InviteAdmin) => {
      analytics.inviteAdmin();
      setSending(true);
      try {
        const result = await requestWithAuth<IAdminDetail>(
          endpoint + '/admins',
          'POST',
          params
        );
        setSuccessed(true);
        setPopupAlert({
          severity: 'success',
          children: `${params.name} を管理者に招待しました`
        });
        setAdminDetails(value => ({
          ...value,
          [result.uid]: result
        }));
      } catch (error) {
        setPopupAlert({
          severity: 'error',
          // @ts-expect-error TODO: Errorのタイプチェックを行う
          children: `エラーが発生し、${params.name} を招待できませんでした。(${error.message})`
        });
      }
      setSending(false);
    },
    [teamId, setPopupAlert, setAdminDetails]
  );

  // ダイアログを閉じる
  const close = React.useCallback(() => {
    props.onClose?.({}, 'escapeKeyDown');
  }, [props.onClose]);

  // 次の人を招待する
  const reset = React.useCallback(() => {
    setSending(false);
    setSuccessed(false);
    setEmail('');
    setName('');
  }, []);

  // URL をコピーする
  const urlRef = React.useRef<HTMLInputElement>(null);
  const [copied, setCopied] = React.useState(false);
  const copy = React.useCallback(() => {
    urlRef.current?.select();
    const copied = document.execCommand('copy');
    setCopied(copied);
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  }, []);

  return (
    <Dialog {...props}>
      {!successed ? (
        <>
          <DialogTitle>管理者を招待する</DialogTitle>
          <DialogContent sx={styles.content}>
            <TextField
              label="メールアドレス"
              onChange={event => setEmail(event.target.value)}
              error={Boolean(email) && invalid}
              helperText="招待するメールアドレス。Google アカウントである必要があります"
              required
              disabled={sending}
            />
            <TextField
              label="表示名"
              onChange={event => setName(event.target.value)}
              helperText="ダッシュボード上で表示される名前です。いつでも変更できます"
              required
              disabled={sending}
            />
          </DialogContent>
          <DialogActions>
            <Button
              color="primary"
              disabled={invalid || !name || sending}
              onClick={() => send({ email, name, teamId })}
            >
              招待する
            </Button>
            <Button onClick={close}>とじる</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>管理者を追加しました</DialogTitle>
          <DialogContent sx={styles.content}>
            <Typography variant="body1">
              {name} をこのチームの管理者に追加しました
            </Typography>
            <Typography variant="body1">
              下のリンクを {name} に送って、Google
              でログインするよう伝えて下さい
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              label="招待用 URL"
              value={`${window.location.origin}?login_hint=${encodeURIComponent(
                email
              )}`}
              inputRef={urlRef}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="コピーしました" open={copied}>
                      <IconButton onClick={copy}>
                        <FileCopy />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
              helperText="右のボタンを押すとコピーできます"
            />
          </DialogContent>
          <DialogActions>
            <Button color="primary" onClick={reset}>
              別の人を招待する
            </Button>
            <Button onClick={close}>とじる</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
